// src/pages/Generating.js
// HomeIQa — Report generation progress page
// Polls Firebase for report status every 5 seconds
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReportStatus } from '../firebase';
import Logo from '../components/Logo';

const MESSAGES = [
  'Verifying your property address against county records...',
  'Pulling comparable sales within 0.5 miles...',
  'Fetching live market data for your ZIP code...',
  'Cross-referencing your purchase price with deed records...',
  'Analyzing market timing and seasonality...',
  'Calculating equity and net proceeds estimate...',
  'Identifying top improvement ROI opportunities...',
  'Generating your personalized report...',
  'Formatting and packaging your PDF...',
  'Almost done — finalizing your report...'
];

export default function Generating() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [msgIdx, setMsgIdx] = useState(0);
  const [dots, setDots] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [failed, setFailed] = useState(false);
  const pollRef = useRef(null);
  const msgRef = useRef(null);
  const dotRef = useRef(null);
  const timeRef = useRef(null);

  useEffect(() => {
    // Cycle through messages
    msgRef.current = setInterval(() => {
      setMsgIdx(i => (i + 1) % MESSAGES.length);
    }, 8000);

    // Animate dots
    dotRef.current = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 500);

    // Track elapsed time
    timeRef.current = setInterval(() => {
      setElapsed(e => e + 1);
    }, 1000);

    // Poll for completion every 5 seconds
    pollRef.current = setInterval(async () => {
      try {
        const result = await getReportStatus({ orderId });
        const { status, reportId } = result.data;
        if (status === 'complete' && reportId) {
          clearAllIntervals();
          navigate(`/report/${reportId}`);
        } else if (status === 'failed') {
          clearAllIntervals();
          setFailed(true);
        }
      } catch (e) {
        console.error('Poll error:', e);
      }
    }, 5000);

    return () => clearAllIntervals();
  }, [orderId, navigate]);

  function clearAllIntervals() {
    [pollRef, msgRef, dotRef, timeRef].forEach(ref => {
      if (ref.current) clearInterval(ref.current);
    });
  }

  const progressPct = Math.min(95, (elapsed / 90) * 100);

  return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>

      <div style={styles.card}>
        {!failed ? (
          <>
            <div style={styles.spinnerWrap}>
              <div style={styles.outerRing}>
                <div style={styles.innerDot} />
              </div>
            </div>

            <div style={styles.title}>Generating your HomeIQa report</div>
            <div style={styles.subtitle}>This typically takes 60–90 seconds. Please keep this window open.</div>

            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
            </div>

            <div style={styles.currentMsg}>
              {MESSAGES[msgIdx]}{dots}
            </div>

            <div style={styles.steps}>
              {['Verify property data', 'Fetch live comps', 'Generate analysis', 'Build PDF', 'Send to email'].map((s, i) => {
                const done = elapsed > i * 18;
                return (
                  <div key={s} style={styles.stepItem}>
                    <div style={{ ...styles.stepDot, background: done ? '#1D9E75' : '#EDE8E0' }} />
                    <span style={{ ...styles.stepLabel, color: done ? '#1C1C1C' : '#9A9A9A' }}>{s}</span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={styles.failedState}>
            <div style={styles.failTitle}>Report generation encountered an error</div>
            <div style={styles.failMsg}>
              Your payment was processed successfully. We are generating your report manually and will email it to you within 2 hours. Order ID: <strong>{orderId}</strong>
            </div>
            <a href="mailto:support@homeIQa.com" style={styles.failLink}>
              Contact support →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" },
  header: { background: '#4A1220', borderBottom: '4px solid #C9A84C', padding: '14px 32px' },
  card: { maxWidth: 520, margin: '64px auto', background: 'white', borderRadius: 16, padding: '48px 44px', textAlign: 'center', boxShadow: '0 4px 32px rgba(0,0,0,0.08)' },
  spinnerWrap: { display: 'flex', justifyContent: 'center', marginBottom: 32 },
  outerRing: { width: 72, height: 72, border: '3px solid #F7F0DC', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 1.2s linear infinite', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  innerDot: { width: 16, height: 16, background: '#6B1E2E', borderRadius: '50%' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: '#4A1220', marginBottom: 8 },
  subtitle: { fontSize: 13, color: '#5A5A5A', marginBottom: 28, lineHeight: 1.6 },
  progressTrack: { height: 6, background: '#EDE8E0', borderRadius: 3, overflow: 'hidden', marginBottom: 16 },
  progressFill: { height: '100%', background: '#6B1E2E', borderRadius: 3, transition: 'width 1s ease' },
  currentMsg: { fontSize: 13, color: '#5A5A5A', minHeight: 20, marginBottom: 32, fontStyle: 'italic' },
  steps: { display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 12 },
  stepDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, transition: 'background 0.5s' },
  stepLabel: { fontSize: 13, transition: 'color 0.5s' },
  failedState: { padding: '8px 0' },
  failTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#4A1220', marginBottom: 12 },
  failMsg: { fontSize: 13, color: '#5A5A5A', lineHeight: 1.7, marginBottom: 20 },
  failLink: { color: '#6B1E2E', fontSize: 14, fontWeight: 500 }
};
